import React, { FC, Fragment, useState } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Technician, User } from 'models/user';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import TechniciansItem from 'components/users/technicians/technicians-list/technicians-item.component';
import UpdateTechniciansDialog from 'components/users/technicians/update-technicians-dialog/update-technicians-dialog.component';
import DelayedLinearProgress from '../../../shared/delayed-linear-progress/delayed-linear-progress.component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type TechniciansListProps = {
    technicians: User[];
    isTechniciansLoading: boolean;
    onFetchMoreChange: () => void;
    onUpdateTechnicianClick: () => void;
};

const TechniciansList: FC<TechniciansListProps> = ({
    technicians,
    isTechniciansLoading,
    onFetchMoreChange,
    onUpdateTechnicianClick,
}: TechniciansListProps) => {
    const { t: translate } = useTranslation(['common']);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [technician, setTechnician] = useState<Technician>({
        _id: '',
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        linkedOrganizations: [],
        isShared: false,
        active: undefined,
    });

    const editTechnicianHandler = (technician: User) => {
        setTechnician({
            _id: technician._id,
            firstName: technician.name.firstName,
            lastName: technician.name.lastName,
            email: technician.email,
            role: technician.role,
            active: technician.active,
            isShared: technician.isShared,
            organization: technician.organization,
            linkedOrganizations: technician.linkedOrganizations,
            salesforceUser: technician.salesforceUser,
            authMethod: technician.authMethod,
            syncid: technician.syncid,
            enableUserSync: technician.enableUserSync,
            personalIdentityNumber: technician.personalIdentityNumber,
        });
        setIsDialogOpen(true);
    };

    const dialogCloseHandler = () => {
        setIsDialogOpen(!isDialogOpen);
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(onFetchMoreChange, technicians?.length || 0);

    return (
        <Fragment>
            {isTechniciansLoading ? <DelayedLinearProgress /> : undefined}
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={technicians.length}
                    next={onFetchMoreChange}
                    hasMore={true}
                    throttleTimeInMs={75}
                    loader={undefined}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">{translate('STATUS')}</TableCell>
                                <TableCell>{translate('common:NAME')}</TableCell>
                                <TableCell>{translate('common:EMAIL')}</TableCell>
                                <TableCell>{translate('common:ROLE')}</TableCell>
                                <TableCell>{translate('LAB')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {technicians ? (
                                <Fragment>
                                    {technicians.map((technician, index) => (
                                        <TechniciansItem
                                            key={index}
                                            technician={technician}
                                            onEditTechnicianClick={editTechnicianHandler}
                                        />
                                    ))}
                                </Fragment>
                            ) : undefined}
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>

            <UpdateTechniciansDialog
                isDialogOpen={isDialogOpen}
                technician={technician}
                onDialogCloseClick={dialogCloseHandler}
                onUpdateTechnicianClick={onUpdateTechnicianClick}
            />
        </Fragment>
    );
};

export default TechniciansList;
